import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';

interface Row {
  telephone: string;
  telephone_display: string;
  email: string;
  whatsapp: string;
  address_he: string;
  opening_hours_display: string;
  instagram: string;
  facebook: string;
}

const EMPTY: Row = {
  telephone: '',
  telephone_display: '',
  email: '',
  whatsapp: '',
  address_he: '',
  opening_hours_display: '',
  instagram: '',
  facebook: '',
};

export default function AdminContacts() {
  const [data, setData] = useState<Row>(EMPTY);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      const { data: row, error } = await supabase
        .from('site_contacts')
        .select('*')
        .eq('id', 'default')
        .maybeSingle();
      if (error) toast.error('שגיאה בטעינת אנשי קשר');
      if (row) {
        setData({
          telephone: row.telephone ?? '',
          telephone_display: row.telephone_display ?? '',
          email: row.email ?? '',
          whatsapp: row.whatsapp ?? '',
          address_he: row.address_he ?? '',
          opening_hours_display: row.opening_hours_display ?? '',
          instagram: row.instagram ?? '',
          facebook: row.facebook ?? '',
        });
      }
      setLoading(false);
    })();
  }, []);

  const update = (k: keyof Row) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
    setData((d) => ({ ...d, [k]: e.target.value }));

  const save = async () => {
    setSaving(true);
    const { error } = await supabase
      .from('site_contacts')
      .upsert({ id: 'default', ...data }, { onConflict: 'id' });
    setSaving(false);
    if (error) {
      toast.error('שמירה נכשלה: ' + error.message);
    } else {
      toast.success('נשמר בהצלחה');
    }
  };

  if (loading) return <div className="text-sm text-muted-foreground">טוען…</div>;

  return (
    <div className="space-y-6 max-w-2xl">
      <div>
        <h1 className="text-2xl font-semibold">פרטי קשר באתר</h1>
        <p className="text-sm text-muted-foreground mt-1">
          שדות אלה מוצגים בפוטר, בעמוד יצירת קשר ובסכמת ה-SEO של האתר.
        </p>
      </div>

      <div className="grid gap-4">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label>טלפון (לחיוג)</Label>
            <Input value={data.telephone} onChange={update('telephone')} placeholder="+972-50-1234567" dir="ltr" />
          </div>
          <div>
            <Label>טלפון (לתצוגה)</Label>
            <Input value={data.telephone_display} onChange={update('telephone_display')} placeholder="050-1234567" dir="ltr" />
          </div>
        </div>

        <div>
          <Label>אימייל</Label>
          <Input value={data.email} onChange={update('email')} placeholder="info@example.com" dir="ltr" />
        </div>

        <div>
          <Label>כתובת</Label>
          <Input value={data.address_he} onChange={update('address_he')} placeholder="מושב פטיש 212" />
        </div>

        <div>
          <Label>שעות פעילות</Label>
          <Textarea
            value={data.opening_hours_display}
            onChange={update('opening_hours_display')}
            rows={3}
            placeholder="א׳-ה׳: 09:00 - 18:00&#10;ו׳: 09:00 - 13:00"
          />
        </div>

        <div>
          <Label>קישור WhatsApp</Label>
          <Input value={data.whatsapp} onChange={update('whatsapp')} placeholder="https://wa.me/972501234567" dir="ltr" />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label>Instagram</Label>
            <Input value={data.instagram} onChange={update('instagram')} placeholder="https://instagram.com/..." dir="ltr" />
          </div>
          <div>
            <Label>Facebook</Label>
            <Input value={data.facebook} onChange={update('facebook')} placeholder="https://facebook.com/..." dir="ltr" />
          </div>
        </div>
      </div>

      <div className="flex justify-end">
        <Button onClick={save} disabled={saving}>
          {saving ? 'שומר…' : 'שמירה'}
        </Button>
      </div>
    </div>
  );
}
